// Shared org chrome for home / library / teams / meetings (calendar, live notes, notes) / admin (reset PR2): the DS
// Sidebar (240px, no drawn window controls: the Tauri app uses native decorations) and a 64px
// top bar with search, Record and the account menu. One sidebar, one top bar, one mobile
// drawer — pages pass their own content and, optionally, extra top-bar controls.
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuSeparator,
  NavItem,
  Sidebar,
  type SidebarProps,
} from "@grackle/ui";
import type { FolderOut, OrgOut, OrgRole, UserOut } from "../api/types";
import { FLAGS } from "../featureFlags";
import FolderFormDialog from "../library/FolderFormDialog";
import { buildSidebarFolders } from "../library/folderTree";
import { listFolders } from "../library/api";
import { SWITCH_ORG_PATH, setLastOrgSlug } from "../lib/lastOrg";
import { useIsMobileLayout } from "../lib/mobileLayout";
import { folderPath, homePath, libraryPath, searchPath, trashPath, uploadPath } from "../library/routes";
import { useSession } from "../session";
import { NotificationBell } from "../notifications/NotificationBell";
import { useIsPlatformAdmin } from "../ops/platformAdmin";
import { MyAccountDialog } from "../pages/AccountPage";
import { MobileShell } from "./MobileShell";
import { initials } from "./ui";
import "./AppShell.css";

/** Reset PR3: live notes and meeting notes are part of Meetings (the 2026-10-08 sidebar
 * decision), so they highlight the Meetings entry rather than having one of their own. */
export type AppSection = "home" | "library" | "shared" | "teams" | "meetings" | "admin" | "trash" | "search" | string;

/** FR-L3a: search covers recordings and their transcripts. Submitting (Enter) opens the
 * transcript search page; typing on the library still filters it live by title/owner. */
export const SEARCH_PLACEHOLDER = "Search recordings and transcripts";

const VERIFY_TO_RECORD = "Verify your email to record";
const VERIFY_TO_UPLOAD = "Verify your email to upload";

const ROLE_LABEL: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  guest: "Guest",
};

/** A plain left click is routed client-side; modified clicks open a tab as a link would. */
function isPlainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export function AppShell({
  org,
  user,
  section,
  canRecord = true,
  topbar,
  fullWidth = false,
  children,
}: {
  org: OrgOut;
  user: UserOut;
  section: AppSection;
  canRecord?: boolean;
  /** Extra top-bar controls, rendered after the search field */
  topbar?: ReactNode;
  /** Use the body's full width (the video pages: Home, Library, Folder, Watch, Notes). Every
   * other page is a centred --page-max-width column. */
  fullWidth?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, orgs = [] } = useSession();
  const mobile = useIsMobileLayout();
  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const platformAdmin = useIsPlatformAdmin();
  const menuRef = useRef<HTMLDivElement>(null);
  const isAdmin = org.role === "owner" || org.role === "admin";
  const base = homePath(org.slug);

  // Persist so TraySync (and anything else outside `/o/:slug`) can keep using this org after
  // the user leaves an org page (e.g. while recording at `/record`).
  useEffect(() => {
    setLastOrgSlug(org.slug);
  }, [org.slug]);

  const libraryRoute = `${base}/library`;
  const onLibrary = location.pathname === libraryRoute;

  // F2: the sidebar's Folders section (grouped by scope, nested to depth 3) and "New folder".
  const [folders, setFolders] = useState<FolderOut[]>([]);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  // Profile, sign-in and security, sessions, data and deletion: a dialog over the current page
  // from the sidebar and the account menu (MyAccountDialog). The phone layout has none; those
  // cards stay on its Settings page.
  const [accountOpen, setAccountOpen] = useState(false);
  const closeAccount = useCallback(() => setAccountOpen(false), []);
  const loadFolders = useCallback(() => {
    if (!FLAGS.folders) return;
    listFolders(org.id)
      .then((res) => setFolders(res.items))
      .catch(() => {
        // Best-effort: the sidebar just shows what it had (or nothing) until the next render.
      });
  }, [org.id]);
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);
  const sidebarFolders = useMemo(() => buildSidebarFolders(folders), [folders]);

  // The top-bar search mirrors the library's ?q= so the field shows what the grid is filtered by.
  const urlQuery = onLibrary ? new URLSearchParams(location.search).get("q") ?? "" : "";
  const [query, setQuery] = useState(urlQuery);
  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setNavOpen(false);
        setMenuOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("nav-lock", navOpen);
    return () => document.body.classList.remove("nav-lock");
  }, [navOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: globalThis.MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  function go(to: string) {
    setNavOpen(false);
    setMenuOpen(false);
    navigate(to);
  }

  // Sidebar ids -> routes. "shared" is the library with FR-L4a's server-side filter=shared.
  const routes: Record<string, string> = {
    home: base,
    library: libraryRoute,
    shared: libraryPath(org.slug, { filter: "shared" }),
    meetings: `${base}/meetings`,
    teams: `${base}/teams`,
    settings: "/account",
    // F11: Ask across meetings.
    ask: `${base}/ask`,
    // F2: trash view, and every folder's own contents page.
    trash: trashPath(org.slug),
  };
  for (const folder of folders) {
    routes[folder.id] = folderPath(org.slug, folder.id);
  }

  const nav: SidebarProps["nav"] = [
    { id: "home", icon: "house", label: "Home" },
    { id: "library", icon: "book-open", label: "Library" },
    { id: "meetings", icon: "users-three", label: "Meetings" },
    // FR-L4a: kept in the sidebar (2026-10-08 decision); recordings granted to me, never my own.
    { id: "shared", icon: "folder-user", label: "Shared with me" },
    ...(FLAGS.askGrackle ? [{ id: "ask", icon: "chats", label: "Ask Grackle" }] : []),
    // Teams stays (2026-10-08 decision: team management lives with the org). Live meeting is
    // no longer its own entry: it is the "Live notes" tab inside Meetings (reset PR3).
    { id: "teams", icon: "users", label: "Teams" },
  ];

  function linkClick(to: string) {
    return (e: MouseEvent<HTMLElement>) => {
      if (!isPlainClick(e)) return;
      e.preventDefault();
      go(to);
    };
  }

  // FR-AD1's console for owners and admins; its pages are the Tabs in AdminLayout.
  const adminNav = isAdmin ? (
    <>
      <div className="gk-overline gk-sidebar__label">Admin</div>
      <nav className="gk-sidebar__group" aria-label="Admin">
        <NavItem
          icon="shield-check"
          label="Admin console"
          href={`${base}/admin`}
          active={section === "admin"}
          onClick={linkClick(`${base}/admin`)}
        />
      </nav>
    </>
  ) : null;

  // F2: typing still live-filters the library by title/owner (onQueryChange, below); submitting
  // (Enter, or the form's implicit submit) is transcript search (FR-L3a) instead of the library's
  // own ?q=. An empty submission just clears the library's filter rather than opening a page the
  // server would 400 (search's q is required, 1-200 chars).
  function onSearch(e: FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) {
      if (onLibrary) go(libraryRoute);
      return;
    }
    go(searchPath(org.slug, q));
  }

  function onQueryChange(value: string) {
    setQuery(value);
    // On the library the grid filters as you type, as it did before the search moved here.
    if (onLibrary) {
      const params = new URLSearchParams(location.search);
      if (value.trim()) params.set("q", value);
      else params.delete("q");
      const s = params.toString();
      navigate(`${libraryRoute}${s ? `?${s}` : ""}`, { replace: true });
    }
  }

  async function onSignOut() {
    setMenuOpen(false);
    await signOut();
    navigate("/signin", { replace: true });
  }

  const displayName = user.name || user.email;

  // PR6: the DS mobile layout (artboards 3a-3e) replaces the sidebar/topbar/drawer wholesale
  // with MobileShell's bottom tab bar — the one switch point, so Home/Library/Meetings keep
  // using AppShell and never have to know which chrome they ended up inside.
  if (mobile) {
    return (
      <MobileShell org={org} section={section} canRecord={canRecord}>
        {children}
      </MobileShell>
    );
  }

  return (
    <div className={`app-shell${navOpen ? " is-nav-open" : ""}`}>
      <div className="app-shell__frame">
      <button
        type="button"
        className="app-shell__scrim"
        aria-label="Close navigation"
        tabIndex={navOpen ? 0 : -1}
        onClick={() => setNavOpen(false)}
      />

      <div className="app-shell__nav" id="app-nav">
        <IconButton
          icon="x"
          label="Close navigation"
          size="sm"
          className="app-shell__nav-close"
          onClick={() => setNavOpen(false)}
        />
        <Sidebar
          windowControls={false}
          active={section}
          nav={nav}
          hrefFor={(id) => routes[id]}
          onNavigate={(id) => {
            const to = routes[id];
            if (to) go(to);
          }}
          extraNav={adminNav}
          folders={FLAGS.folders ? sidebarFolders : undefined}
          onNewFolder={FLAGS.folders ? () => setNewFolderOpen(true) : undefined}
          onMyAccount={() => {
            setNavOpen(false);
            setAccountOpen(true);
          }}
          /* No usage prop: the meter stays hidden until quotas (O4) give FLAGS.usageMeter data. */
          org={{
            name: org.name,
            initial: org.name.trim().slice(0, 1).toUpperCase(),
            detail: ROLE_LABEL[org.role] ?? org.role,
          }}
          onOpenOrg={() => go(SWITCH_ORG_PATH)}
          /* DS WorkspaceSwitcher quick menu: the orgs this account belongs to, then the actions
             the prototype hangs off the switcher (invite, workspace settings, create). */
          orgMenu={[
            ...orgs.map((o) => ({
              id: `org:${o.slug}`,
              label: o.name,
              meta: ROLE_LABEL[o.role] ?? o.role,
              checked: o.id === org.id,
            })),
            { divider: true as const },
            ...(isAdmin
              ? [
                  { id: "invite", label: "Invite people", icon: "user-plus" },
                  { id: "settings", label: "Workspace settings", icon: "gear-six" },
                ]
              : []),
            { id: "new", label: "Create a workspace", icon: "plus" },
          ]}
          onOrgMenuSelect={(id) => {
            if (id.startsWith("org:")) {
              const slug = id.slice(4);
              if (slug !== org.slug) go(homePath(slug));
            } else if (id === "invite") go(`${base}/admin/members`);
            else if (id === "settings") go(`${base}/admin/settings`);
            else if (id === "new") go("/orgs/new");
          }}
        />
      </div>

      <div className="app-shell__main">
        <header className="app-topbar">
          <IconButton
            icon="list"
            label="Open navigation"
            variant="outline"
            size="sm"
            className="app-topbar__menu"
            aria-expanded={navOpen}
            aria-controls="app-nav"
            onClick={() => setNavOpen(true)}
          />
          <form role="search" className="app-topbar__search" onSubmit={onSearch}>
            <Input
              icon="magnifying-glass"
              type="search"
              name="q"
              placeholder={SEARCH_PLACEHOLDER}
              aria-label={SEARCH_PLACEHOLDER}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              wrapperStyle={{ width: "100%" }}
            />
          </form>
          {topbar}
          <div className="app-topbar__end">
            <NotificationBell />
            {/* FR-P14: upload an existing file — secondary to "Record a video", same verified-email gate. */}
            <Button
              variant="secondary"
              icon="cloud-arrow-up"
              disabled={!canRecord}
              title={canRecord ? undefined : VERIFY_TO_UPLOAD}
              onClick={() => go(uploadPath(org.slug))}
            >
              Upload
            </Button>
            {/* The DS's ink record pill, moved from the bottom of the rail to the top bar, where the
                DS drew a purple "New video" that opened the same /record page: one record action,
                set apart from the secondary buttons beside it. The rail's copy stays out (no
                onNewRecording); its Alt+Shift+R hint was never wired to anything. */}
            <Button
              variant="dark"
              icon="video-camera"
              disabled={!canRecord}
              title={canRecord ? undefined : VERIFY_TO_RECORD}
              onClick={() => go("/record")}
            >
              Record a video
            </Button>
            <div className="app-topbar__account" ref={menuRef}>
              <button
                type="button"
                className="app-topbar__avatar"
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <Avatar name={displayName} initials={initials(user.name, user.email)} size={36} speaker={1} />
              </button>
              {menuOpen ? (
                <Menu
                  className="app-topbar__menu-pop"
                  header={
                    <div className="app-topbar__menu-head">
                      <span className="app-topbar__menu-name">{displayName}</span>
                      <span className="app-topbar__menu-email">{user.email}</span>
                    </div>
                  }
                >
                  <MenuItem
                    icon="user"
                    onClick={() => {
                      setMenuOpen(false);
                      setAccountOpen(true);
                    }}
                  >
                    My account
                  </MenuItem>
                  <MenuItem icon="gear-six" onClick={() => go("/account")}>
                    Settings
                  </MenuItem>
                  <MenuItem icon="arrows-left-right" onClick={() => go(SWITCH_ORG_PATH)}>
                    Switch organization
                  </MenuItem>
                  {/* FR-AD7: staff only — never rendered for anyone without a platform_admins row. */}
                  {platformAdmin ? (
                    <MenuItem icon="shield-check" onClick={() => go("/ops")}>
                      Operator console
                    </MenuItem>
                  ) : null}
                  <MenuSeparator />
                  <MenuItem icon="sign-out" onClick={() => void onSignOut()}>
                    Sign out
                  </MenuItem>
                </Menu>
              ) : null}
            </div>
          </div>
        </header>
        <div className={`app-shell__content${fullWidth ? "" : " app-shell__content--capped"}`}>{children}</div>
      </div>
      </div>

      <MyAccountDialog open={accountOpen} onClose={closeAccount} />

      {newFolderOpen ? (
        <FolderFormDialog
          mode="create"
          org={org}
          onClose={() => setNewFolderOpen(false)}
          onSaved={() => {
            setNewFolderOpen(false);
            loadFolders();
          }}
        />
      ) : null}
    </div>
  );
}
